import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import buildAffiliateLink from 'lib/buildAffiliateLink';

import { isLengthGreaterThan8 } from 'lib/fontBasedOnTextLength';
import { getDateTime } from 'lib/DateTime';
import ShopButton from './ShopButton';

import styles from './styles.module.scss';

const ENCODER_STOP_DATE = 'Wed Apr 15 2020 00:00:00';

const ShopButtonInfo = (
  {
    name,
    listPrice,
    salePrice,
    productUrl,
    seller,
    additionalStyles,
    block,
    isLink,
    articlePublishDate,
  }, { canonicalUrl },
) => {
  /**
    If an articlePublishDate is provided we need to determine if the articlePublishDate
    is after the ENCODER_STOP_DATE (isCurrentArticlePublishedAfterEncoderStopDate)
    because then we wouldn't need to build the affiliate link in Ramen
  */
  let isCurrentArticlePublishedAfterEncoderStopDate;
  if (articlePublishDate) {
    const fetchedEncoderStopDate = getDateTime(ENCODER_STOP_DATE);
    const fetchedCurrentArticlePublishDate = getDateTime(articlePublishDate);
    // eslint-disable-next-line max-len
    isCurrentArticlePublishedAfterEncoderStopDate = fetchedCurrentArticlePublishDate > fetchedEncoderStopDate;
  }
  const fontToolkit = 'founders-mono';
  const priceToolkit = ['df', 'items-center', 'justify-center', 'bg-transparent', 'fw5', 'flex-shrink-0'];
  const isWaffle = block === 'WaffleProduct';
  const affiliateUrl = isCurrentArticlePublishedAfterEncoderStopDate
    ? productUrl
    : buildAffiliateLink(productUrl, canonicalUrl);

  const isLongerThan8Chars = isLengthGreaterThan8(seller);
  const bemElemPriceBtn = `${block}__priceBtn`;
  const costStyles = [bemElemPriceBtn, styles.cost, additionalStyles.cost,
    { [styles.costFitText]: isLongerThan8Chars }, priceToolkit, fontToolkit];
  const body = (
    <React.Fragment>
      <span className={classNames(costStyles)}>
        {salePrice && (
          <div className="red b">
            $
            {salePrice}
          </div>
        )}
        <div className={classNames('gray-60 b', { 'strike fw5': salePrice })}>
          $
          {listPrice}
        </div>
      </span>
      <ShopButton
        additionalStyles={additionalStyles}
        block={block}
        fontToolkit={fontToolkit}
        isWaffle={isWaffle}
        listPrice={listPrice}
        name={name}
        seller={seller}
      />
    </React.Fragment>
  );
  if (isLink) {
    return (
      <a
        className={classNames(`${block}__shopButtons`, styles.shop, { [styles.shopFitText]: isLongerThan8Chars }, additionalStyles.shop, 'df')}
        href={affiliateUrl}
        rel="noopener"
        target="_blank" // eslint-disable-line react/jsx-no-target-blank
        key={affiliateUrl}
      >
        {body}
      </a>
    );
  }
  return (
    <div
      className={classNames(`${block}__shopButtons`, styles.shop, { [styles.shopFitText]: isLongerThan8Chars }, additionalStyles.shop, 'df')}
      key={affiliateUrl}
    >
      {body}
    </div>
  );
};

ShopButtonInfo.propTypes = {
  name: PropTypes.string.isRequired,
  listPrice: PropTypes.string.isRequired,
  salePrice: PropTypes.string,
  articlePublishDate: PropTypes.string,
  productUrl: PropTypes.string.isRequired,
  seller: PropTypes.string,
  additionalStyles: PropTypes.shape({
    shop: PropTypes.string,
    cost: PropTypes.string,
    vendor: PropTypes.string,
    icon: PropTypes.string,
    vendorName: PropTypes.string,
  }),
  block: PropTypes.string,
  isLink: PropTypes.bool,
};

ShopButtonInfo.defaultProps = {
  seller: null,
  isLink: true,
  salePrice: '',
  articlePublishDate: '',
  additionalStyles: PropTypes.shape({
    shop: '',
    cost: '',
    vendor: '',
    icon: '',
    vendorName: '',
  }),
  block: 'ListicleProduct',
};

ShopButtonInfo.contextTypes = {
  canonicalUrl: PropTypes.string,
};

export default ShopButtonInfo;
